import { compareVersions } from 'compare-versions';
import React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { releaseVersion } from '@gitbook/env';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { localStorage } from '@gitbook/util-local-storage';

import { apiClient, calculateDaysDifference, usePageVisibility } from '../../state';

const UPDATE_THRESHOLD = 7; // days

/**
 * Button in the sidebar footer to notify the user that a new version of the app is available,
 * reloading the app to update it when pressed.
 */
export function AppVersionAlert(): React.ReactElement | null {
    const availableVersion = useAvailableAppVersion();

    if (!availableVersion) {
        return null;
    }

    return (
        <Button
            testID="sidebar.appVersionAlert"
            kind="secondary"
            size="small"
            label="New version available"
            title="Reload to update"
            leadingIcon={Icon.Refresh}
            onPress={() => {
                analytics.logEvent({
                    type: 'app_version_update',
                    previousVersion: releaseVersion,
                    currentVersion: availableVersion,
                });
                localStorage.set('app_version_updated', [
                    availableVersion,
                    new Date().toISOString(),
                ]);
                window.location.reload();
            }}
        />
    );
}

/**
 * Return the version served by the API when it is newer than the running one.
 * The check runs again every time the page becomes visible, to catch releases
 * happening while the tab sits in the background.
 */
function useAvailableAppVersion(): string | null {
    const visible = usePageVisibility();
    const [availableVersion, setAvailableVersion] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!visible || hasRecentlyUpdated()) {
            return;
        }

        let cancelled = false;
        apiClient.system
            .getApiInformation()
            .then(({ data: apiInfo }) => {
                /* compare versions, -1 means the releaseVersion is less than the current version in api info */
                if (!cancelled && compareVersions(releaseVersion, apiInfo.version) === -1) {
                    setAvailableVersion(apiInfo.version);
                }
            })
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [visible]);

    return availableVersion;
}

/**
 * Whether the app has been updated recently, in which case the user is not prompted again
 * for a few days.
 */
function hasRecentlyUpdated(): boolean {
    const lastUpdated = localStorage.get<[string, string]>('app_version_updated');
    if (!lastUpdated) {
        return false;
    }

    const [lastVersion, lastDate] = lastUpdated;
    return (
        compareVersions(releaseVersion, lastVersion) === 0 ||
        calculateDaysDifference(new Date(lastDate), new Date()) < UPDATE_THRESHOLD
    );
}
